import React from 'react';
import {
  Search,
  SlidersHorizontal,
  Plus,
  Grid3X3,
  List,
  RefreshCw,
  Download,
  Trash2,
  Loader2,
} from 'lucide-react';
import type { RefreshProgress } from '@/hooks/useInfluencerRefresh';
import { Button } from '@/components/ui/button';
import { ExportButton } from '@/shared/ui/export/ExportButton';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface InfluencerCRMHeaderProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  viewMode: 'table' | 'grid';
  onViewModeChange: (mode: 'table' | 'grid') => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  selectedCount: number;
  isPolling: boolean;
  isEnqueueing: boolean;
  canRefreshSelected: boolean;
  refreshSelectedCount: number;
  maxSelectedRefreshes: number;
  refreshProgress: RefreshProgress | null;
  onRefreshSelected: () => void;
  onRequestSelectInfluencers: () => void;
  onExport: () => void;
  onAddToCampaign: () => void;
  onAddInfluencer: () => void;
  onDeleteSelected: () => void;
}

export const InfluencerCRMHeader: React.FC<InfluencerCRMHeaderProps> = ({
  searchTerm,
  onSearchChange,
  viewMode,
  onViewModeChange,
  showFilters,
  onToggleFilters,
  selectedCount,
  isPolling,
  isEnqueueing,
  canRefreshSelected,
  refreshSelectedCount,
  maxSelectedRefreshes,
  refreshProgress,
  onRefreshSelected,
  onRequestSelectInfluencers,
  onExport,
  onAddToCampaign,
  onAddInfluencer,
  onDeleteSelected,
}) => {
  return (
    <div className="bg-white border-b border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Influencers</h1>
          <p className="text-gray-600">Discover, manage, and analyze your influencer relationships</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedCount > 0 ? (
            isPolling ? (
              <Button
                variant="outline"
                disabled
                title="Refresh in progress…"
              >
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {refreshProgress
                  ? `Refreshing ${refreshProgress.processedCount}/${refreshProgress.totalCount}…`
                  : 'Refreshing…'}
              </Button>
            ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={isEnqueueing || !canRefreshSelected}
                  title={
                    canRefreshSelected
                      ? `Refresh selected influencers (${maxSelectedRefreshes - refreshSelectedCount} remaining today)`
                      : `You can only refresh influencer data ${maxSelectedRefreshes} times a day`
                  }
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh All
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Refresh {selectedCount} selected influencer{selectedCount === 1 ? '' : 's'}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will update profile data and metrics for the selected influencer{selectedCount === 1 ? '' : 's'} by calling both refresh APIs. This counts as one of your {maxSelectedRefreshes} daily refreshes.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onRefreshSelected}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            )
          ) : (
            <Button
              variant="outline"
              onClick={onRequestSelectInfluencers}
              disabled={isPolling || isEnqueueing || !canRefreshSelected}
              title={
                canRefreshSelected
                  ? "Select influencers before refreshing"
                  : `You can only refresh influencer data ${maxSelectedRefreshes} times a day`
              }
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh All
            </Button>
          )}
          <ExportButton
            variant="outline"
            iconType="download"
            onClick={onExport}
          />
          {selectedCount > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={onAddToCampaign}>
                <Plus className="w-4 h-4 mr-1" />
                Add to Campaign
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={onDeleteSelected}
              >
                <Trash2 className="w-4 h-4 mr-1 text-white" />
                Delete
              </Button>
            </>
          )}

          {/* + Add Influencer Button */}
          <Button
            onClick={onAddInfluencer}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Influencer
          </Button>
        </div>
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-xl relative">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <Input
            type="text"
            placeholder="Search influencers by name, handle, or category..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-10 border-[#CFC2D6]"
          />
          <button
            onClick={onToggleFilters}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors focus:outline-none ${showFilters ? 'text-[#A655F8]' : 'text-gray-400 hover:text-gray-600'
              }`}
            title="Filters"
          >
            <SlidersHorizontal className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('table')}
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('grid')}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
